import type { IsoDate } from "./types.js";
import { SEASON_2 } from "./plan.js";
import { addDays } from "./dates.js";

/**
 * 台北馬賽季競猜的日期與數值規則。前後端共用——前端拿來決定介面狀態，後端拿來
 * 強制執行。與 `src/domain/` 同一條鐵則：**不讀系統時間**，「今天」由呼叫端傳入。
 *
 * 生日賽季的規則在 `shared/prediction.ts`，已封盤，不要併進來。
 */

/**
 * 2026 臺北馬拉松半程的官方規定。來源：
 * https://www.taipeicitymarathon.com/REGULATION202612.php（2026-11-05 查閱，大會保留修改權）
 */
export const RACE_START_TIME = "07:00";
export const RACE_LIMIT_HOURS = 3;
export const RACE_CHECKPOINTS = [
  { km: 7.1, closes: "08:00" },
  { km: 11.1, closes: "08:34" },
  { km: 17.9, closes: "09:32" }
] as const;

/**
 * 猜的與補登的完賽時間共用這個範圍（秒）。上限就是官方限時，超過限時算未完賽。
 * 資料庫不檢查，由端點驗證，見 `docs/adr/0009`。
 */
export const FINISH_RANGE = [3600, RACE_LIMIT_HOURS * 3600] as const;
export const FINISH_RANGE_MESSAGE = `完賽時間要介於 1 到 ${RACE_LIMIT_HOURS} 小時之間`;

/** 官方成績可能晚一點才公布，所以賽後還留幾天可以補。 */
const RESULT_EDIT_DAYS = 7;

/** 開賽才收，比賽當天不收——起跑之後就是看著答案下注了。 */
export function canBetOnRace(today: IsoDate): boolean {
  return today >= SEASON_2.start && today < SEASON_2.end;
}

/** 完賽成績只有賽後一小段時間能填，這是賽季封存唯一的例外，見 CONTEXT.md。 */
export function canEditRaceResult(today: IsoDate): boolean {
  return today >= SEASON_2.end && today <= addDays(SEASON_2.end, RESULT_EDIT_DAYS);
}

export function validFinishSeconds(value: unknown): value is number {
  return Number.isInteger(value)
    && (value as number) >= FINISH_RANGE[0]
    && (value as number) <= FINISH_RANGE[1];
}

/**
 * `時:分` 或 `時:分:秒` 轉成秒。看不懂回 null。
 *
 * 只收冒號格式：`2.5` 可能是兩個半小時也可能是兩小時五分，猜錯的話排名就錯了，
 * 不如請人重打。
 */
export function parseFinishTime(raw: string): number | null {
  const match = raw.trim().match(/^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!match) return null;
  return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] ?? 0);
}

/**
 * 分開填的時、分、秒組回 `parseFinishTime()` 看得懂的字串，全部沒填是空字串。
 *
 * 手機的數字鍵盤沒有冒號，所以畫面是分開的格子。分和秒只填一位時補零：`5` 是 5 分，
 * 不是 50 分。小時沒填會組出看不懂的字串，由 `parseFinishTime()` 擋下來。
 */
export function joinFinishTime(hours: string, minutes: string, seconds = ""): string {
  const [h, m, s] = [hours, minutes, seconds].map(part => part.trim());
  if (!h && !m && !s) return "";
  return `${h}:${m.padStart(2, "0")}:${s.padStart(2, "0")}`;
}

/** 秒拆成畫面上那幾格。沒有時間時每一格都是空的。 */
export function finishTimeParts(seconds: number | null | undefined) {
  if (!seconds) return { hours: "", minutes: "", seconds: "" };
  const [hours, minutes, rest] = formatFinishTime(seconds).split(":");
  return { hours, minutes, seconds: rest };
}

/** 秒轉成 `時:分:秒`。 */
export function formatFinishTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${hours}:${pad(minutes)}:${pad(rest)}`;
}
